"use client";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { IconWarning } from "@teral/icons";
import React from "react";
import { cn } from "../../lib/utils";
import { Button } from "../buttons/button";
import { Popover, PopoverContent, type PopoverContentProps } from "./popover";

const PopoverClose = PopoverPrimitive.Close;

type ConfirmVariant = "warning" | "danger";

type ConfirmPopoverProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  title?: string;
  description?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  variant?: ConfirmVariant;
  popoverProps?: Partial<PopoverContentProps>;
};

const VARIANT_STYLES = {
  warning: {
    iconBg: "bg-warningA-4",
    iconColor: "text-warning-11",
    buttonColor: "warning" as const,
    icon: IconWarning,
  },
  danger: {
    iconBg: "bg-error-4",
    iconColor: "text-error-11",
    buttonColor: "danger" as const,
    icon: IconWarning,
  },
};

const DEFAULT_POPOVER_PROPS = {
  sideOffset: 5,
  side: "bottom" as const,
  align: "center" as const,
  className:
    "flex flex-col items-center justify-center overflow-hidden rounded-xl! p-0 gap-0 min-w-[344px]",
  initialFocus: false,
};

export const ConfirmPopover = ({
  isOpen,
  onOpenChange,
  onConfirm,
  triggerRef,
  title = "Confirm action",
  description = "Are you sure you want to perform this action?",
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  variant = "warning",
  popoverProps = {},
}: ConfirmPopoverProps): React.ReactElement => {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const { iconBg, iconColor, buttonColor, icon: Icon } = VARIANT_STYLES[variant];

  const mergedPopoverProps = {
    ...DEFAULT_POPOVER_PROPS,
    ...popoverProps,
    className: cn(DEFAULT_POPOVER_PROPS.className, popoverProps.className),
  };

  const safeRef = React.useMemo(
    () => ({
      get current() {
        return triggerRef.current ?? document.body;
      },
    }),
    [triggerRef],
  );

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverContent anchor={safeRef} {...mergedPopoverProps}>
        <div className="p-4 w-full">
          <div className="flex gap-3 items-center justify-start">
            <div
              className={cn(
                "flex items-center justify-center rounded-sm size-[22px]",
                iconBg,
                iconColor,
              )}
            >
              <Icon className="size-3" />
            </div>
            <div className="font-medium text-sm leading-7 text-gray-12">{title}</div>
          </div>
        </div>
        <div className="w-full">
          <div className="h-px bg-grayA-3 w-full" />
        </div>
        <div className="px-4 w-full text-gray-11 text-sm leading-6 my-4">{description}</div>
        <div className="space-x-3 w-full px-4 pb-4">
          <Button
            color={buttonColor}
            onClick={(e) => {
              e.stopPropagation();
              handleConfirm();
            }}
            className="px-4"
          >
            {confirmButtonText}
          </Button>
          <PopoverClose
            render={
              <Button variant="ghost" className="text-gray-9 px-4">
                {cancelButtonText}
              </Button>
            }
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
